import { useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import CustomTable from '../../../components/customtable/CustomTable';
import DetailCard from '../../../components/detailCard/DetailCard';
import Loader from '../../../components/loader/Loader';
import { PROTECTED_ROUTES } from '../../../constants/route';
import { useTablePaginationHook } from '../../../hooks/tablePagination';
import { reportNameFormat } from '../../../utils/commonUtils';
import { formatMonthName } from '../HFReport';
import './ChwList.scss';
import { AlertModal, DHISLoginModal, ReportViewModal, ReviewModal } from './components/ChwListModals';
import { useChwList } from './hooks/useChwList';
import { IMatchParams } from './types';

const ChwList = (): React.ReactElement => {
  const history = useHistory();
  const { listParams, handleSearch, handlePage } = useTablePaginationHook();
  const { regionId, regionName, siteId, siteName, reportType } = useParams<IMatchParams>();

  const {
    // State
    userList,
    loading,
    selectedUser,
    setSelectedUser,
    selectedAllUser,
    setSelectedAllUser,
    authenticationError,
    chwUserListloading,
    chwUserListCount,

    // Modal states
    reportViewModal,
    setReportViewModal,
    dhisLoginModal,
    setDhisLoginModal,
    reviewModal,
    setReviewModal,
    alertModal,
    setAlertModal,

    // Derived state
    isHF4User,
    pendingSelectedUsers,
    pendingUsersList,

    // Params and data
    month,
    year,

    // Methods
    fetchChwList,
    handleReportView,
    handleReportDownload,
    handleDhisLoginSubmit,
    handleOpenActionModal,
    handleReviewSubmit
  } = useChwList();

  useEffect(() => {
    fetchChwList(listParams);
  }, [fetchChwList, listParams]);

  /**
   * Handle Done button click
   */
  const handleDoneClick = useCallback(() => {
    setSelectedAllUser(false);
    setSelectedUser({});

    history.push(
      PROTECTED_ROUTES.HFReport.replace(':regionId', regionId)
        .replace(':regionName', regionName)
        .replace(':siteId', siteId)
        .replace(':siteName', siteName.replace(/\//g, ''))
        .replace(':reportType', reportType)
    );
  }, [history, regionId, regionName, reportType, siteId, siteName, setSelectedAllUser, setSelectedUser]);

  /**
   * Table column definitions
   */
  const columnsDef = [
    {
      id: 1,
      name: 'name',
      label: 'CHW NAME'
    },
    {
      id: 2,
      name: 'userUnitId',
      label: 'DHIS2 ID'
    },
    {
      id: 3,
      name: 'phoneNumber',
      label: 'CHW MOBILE NO',
      class: 'numeric',
      cellFormatter: (value: any) => `${value?.countryCode && '+' + value.countryCode} ${value?.phoneNumber}`
    }
  ];

  return (
    <>
      {(loading || chwUserListloading) && <Loader />}

      <div className='chw-list-container col-12'>
        <DetailCard
          header={`${reportNameFormat(reportType)} - ${formatMonthName(month)} ${year}`}
          isSearch={true}
          onSearch={handleSearch}
        >
          <CustomTable
            rowData={userList}
            headerOrder={['select', 'column', 'action', 'status']}
            rowDisableKey={isHF4User ? 'reviewed' : 'dhisPushed'}
            checkboxEnableKey={isHF4User ? 'enabled' : undefined}
            disableCheckboxWithRow={true}
            columnsDef={columnsDef}
            isDelete={false}
            isEdit={false}
            isView={true}
            selectAllText='SELECT ALL'
            viewToolTip='View'
            isDownload={true}
            page={listParams.page}
            rowsPerPage={listParams.rowsPerPage}
            count={chwUserListCount}
            checkboxState={{ selectedUser, setSelectedUser }}
            selectAllState={{ selectedAllUser, setSelectedAllUser }}
            handlePageChange={handlePage}
            onRowView={handleReportView}
            onRowDownload={handleReportDownload}
          />
        </DetailCard>
      </div>

      <div className='dhis-action-footer'>
        <div className='col-6'>
          <button
            className='col-5 btn secondary-btn'
            disabled={!pendingUsersList.length || (!pendingSelectedUsers.length && !selectedAllUser)}
            onClick={handleOpenActionModal}
          >
            {isHF4User ? 'Review and Submit' : 'Send to DHIS2'}
          </button>
          <button className='col-5 btn primary-btn' onClick={handleDoneClick}>
            Done
          </button>
        </div>
      </div>

      {/* Modals */}
      <ReportViewModal
        show={reportViewModal.show}
        data={reportViewModal.data}
        reportType={reportType}
        onClose={() => setReportViewModal({ show: false, data: {} })}
      />

      <DHISLoginModal
        show={dhisLoginModal.show}
        data={dhisLoginModal.data}
        authenticationError={authenticationError}
        onClose={() => {
          setDhisLoginModal({ data: {}, show: false });
        }}
        onSubmit={handleDhisLoginSubmit}
      />

      <ReviewModal
        show={reviewModal.show}
        data={selectedUser}
        onClose={() => {
          setReviewModal({ data: {}, show: false });
        }}
        onSubmit={handleReviewSubmit}
      />

      <AlertModal
        show={alertModal.show}
        status={alertModal.status}
        message={alertModal.message}
        onClose={() => {
          fetchChwList(listParams);
          setAlertModal({
            status: '',
            message: '',
            show: false
          });
        }}
      />
    </>
  );
};

export default ChwList;
